import { useEffect, useContext, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../../utils/contexts/UserContext";
import Avatar from "../../Avatar/Avatar";
import { roleNamesLabel } from "../../../utils/permissions";
import { visibleNavItems } from "../navItems";
import { confirmLeaveUnsavedWork } from "../../../utils/helpers/unsavedWork";
import useSupportStore from "../../../utils/stores/useSupportStore";
import telosWordmarkWhite from "../../../assets/telos-wordmark-white.svg";

// Shared focus ring, tuned for the dark slate-900 rail (offset blends in).
const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900";

// One 200ms ease-out for every open/close transition (rail width, labels) so the
// whole rail moves in lockstep. Compose after the `transition-<prop>` class.
const MOTION = "duration-200 ease-out motion-reduce:transition-none";

// Leading-element slot: a fixed-width box that centres the nav icon / avatar on
// the rail's centre line. Width never changes, so the icon column stays put when
// the rail opens (only the label, to its right, is revealed).
const ICON_SLOT = "w-12 flex items-center justify-center shrink-0";

const STORAGE_KEY = "sidebarExpanded";

// The support badge is the one "you have a reply" signal every role sees (the
// notification bell lives on the teacher dashboard only), so it re-asks on
// mount, whenever the window regains focus, and on this interval.
const SUPPORT_UNREAD_POLL_MS = 2 * 60 * 1000;
const SUPPORT_ICON_PATH =
  "M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z";

// Tailwind's `md`. Below it the rail overlays instead of sharing the width.
const SMALL_SCREEN_QUERY = "(max-width: 767px)";
const isSmallViewport = () =>
  typeof window !== "undefined" && window.matchMedia(SMALL_SCREEN_QUERY).matches;

// Collapsed-state hover tooltip — a dark pill to the right of the icon, like the
// claude.ai rail. Decorative (the row's hidden label is the accessible name).
function RailTooltip({ label }) {
  return (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2 py-1 rounded-md bg-slate-800 text-white text-xs font-medium whitespace-nowrap shadow-lg ring-1 ring-black/30 opacity-0 group-hover:opacity-100 transition-opacity z-50"
    >
      {label}
    </span>
  );
}

export default function Sidebar({ onLogout }) {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const location = useLocation();
  // Below `md` the rail is a DRAWER, not a column: it overlays the page
  // instead of taking a share of it. On a 390px phone the expanded rail is
  // 256 of those pixels, which left the observation form 134px — every field
  // truncated to an ellipsis on the one screen an observer uses in a classroom.
  const [isSmallScreen, setIsSmallScreen] = useState(isSmallViewport);
  const [isExpanded, setIsExpanded] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    // A phone starts closed whatever the desktop preference says.
    if (isSmallViewport()) return false;
    return saved === null ? true : saved === "true";
  });

  // Rotating a tablet, or dragging a window across the breakpoint, must not
  // leave a drawer behaving like a column (or the reverse).
  useEffect(() => {
    const mq = window.matchMedia(SMALL_SCREEN_QUERY);
    const onChange = (e) => {
      setIsSmallScreen(e.matches);
      if (e.matches) setIsExpanded(false);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Tapping a destination in a drawer means "go there", not "go there and
  // leave this covering the page".
  useEffect(() => {
    if (isSmallScreen) setIsExpanded(false);
  }, [location.pathname, isSmallScreen]);

  const supportUnread = useSupportStore((s) => s.unreadCount);
  const refreshSupportUnread = useSupportStore((s) => s.refreshUnread);
  useEffect(() => {
    if (!user?.uuid) return undefined;
    refreshSupportUnread();
    const onFocus = () => refreshSupportUnread();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") refreshSupportUnread();
    }, SUPPORT_UNREAD_POLL_MS);
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [user?.uuid, refreshSupportUnread]);
  // Signing out asks the API where to send the browser before it sends it, so
  // the click is followed by a round-trip in which nothing visibly happens.
  // Never reset: whichever way the answer goes, this component is on its way
  // out — either the document is replaced or BaseLayout unmounts.
  const [signingOut, setSigningOut] = useState(false);
  const handleLogout = () => {
    if (signingOut) return;
    if (!confirmLeaveUnsavedWork(t("common.unsavedLeaveConfirm"))) return;
    setSigningOut(true);
    onLogout();
  };

  // A rail link swaps the routed tree without unloading the document, so the
  // browser's own "leave site?" prompt never fires and an in-progress
  // observation or template draft goes with it. Ask before the NavLink
  // navigates; preventing the click leaves the person where they were.
  const guardNavigation = (e) => {
    if (!confirmLeaveUnsavedWork(t("common.unsavedLeaveConfirm"))) {
      e.preventDefault();
    }
  };

  const toggle = () =>
    setIsExpanded((v) => {
      // Opening a drawer for a moment on a phone is not a preference; storing
      // it would follow the person back to their desktop.
      if (!isSmallScreen) localStorage.setItem(STORAGE_KEY, String(!v));
      return !v;
    });

  const displayName =
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    user?.username ||
    t("nav.defaultName");
  const displayRole = roleNamesLabel(user) || t("nav.defaultRole");

  const items = visibleNavItems(user);

  // The label is a flex-1 item whose max-width animates 0 → full (a length,
  // which animates smoothly where flex-grow / width:auto do not). The icon↔label
  // gap comes from the fixed ICON_SLOT, so no margin is animated here.
  const labelClass = `min-w-0 flex-1 truncate transition-all ${MOTION} ${
    isExpanded ? "max-w-[220px] opacity-100 pr-3" : "max-w-0 opacity-0"
  }`;

  // On a phone the OPEN rail leaves the flow and floats over the page; the
  // closed 72px rail stays in it, so the layout below never has to know the
  // difference. `left-0 inset-y-0` rather than a transform: the rail is not
  // rendered off-screen, it is simply lifted out of the column.
  const isDrawer = isSmallScreen && isExpanded;

  return (
    <>
      {isDrawer && (
        <>
          {/* Holds the 72px the closed rail occupied, so opening the drawer
              lifts it out of the column without the page shifting under it. */}
          <div className="w-[72px] shrink-0" aria-hidden="true" />
          <div
            className="fixed inset-0 z-30 bg-slate-900/50"
            onClick={toggle}
            aria-hidden="true"
          />
        </>
      )}
      <aside
      className={`bg-slate-900 text-slate-300 flex flex-col shrink-0 z-40 transition-[width] ${MOTION} shadow-2xl ${
        isExpanded ? "w-64" : "w-[72px]"
      } ${isDrawer ? "fixed inset-y-0 left-0" : ""}`}
    >
      {/* min-w-0 lets children shrink; no overflow clip here so collapsed
          tooltips can escape the rail to the right. */}
      <div className="flex-1 flex flex-col min-w-0 px-3 py-5">
        {/* Header: wordmark (expanded only — no logo when collapsed) + the
            open/close toggle. When collapsed the toggle is a full-width row so
            its icon sits in the same ICON_SLOT column as the nav icons below. */}
        <div className="flex items-center mb-6 h-10">
          {isExpanded && (
            // ml matches a nav icon's inset within ICON_SLOT ((48-20)/2 = 14px)
            // so the wordmark lines up with the item icon column, not the row's
            // (selected-bg) left edge.
            <img
              src={telosWordmarkWhite}
              alt="Telos"
              className="h-10 w-auto ml-3.5"
            />
          )}
          <button
            type="button"
            onClick={toggle}
            title={isExpanded ? t("nav.collapseSidebar") : undefined}
            aria-label={
              isExpanded ? t("nav.collapseSidebar") : t("nav.expandSidebar")
            }
            aria-expanded={isExpanded}
            className={`group relative flex items-center h-10 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors ${FOCUS_RING} ${
              isExpanded ? "ml-auto px-2" : "w-full"
            }`}
          >
            <span className={isExpanded ? "" : ICON_SLOT}>
              {/* Claude-style sidebar panel icon (box with a divider). */}
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <rect x="3" y="3" width="18" height="18" rx="2.5" strokeWidth="2" />
                <path d="M9 3v18" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </span>
            {!isExpanded && <RailTooltip label={t("nav.expandSidebar")} />}
          </button>
        </div>

        {/* Scrolls vertically when expanded; when collapsed, overflow stays
            visible so the hover tooltips aren't clipped by the rail edge. */}
        <nav
          className={`space-y-1 flex-1 min-h-0 ${
            isExpanded
              ? "overflow-y-auto overflow-x-hidden"
              : "overflow-visible"
          }`}
        >
          {items.map((item, index) => {
            const label = t(item.labelKey);
            // A section header renders above the first item of each section.
            const sectionStart =
              item.section && item.section !== items[index - 1]?.section;
            // A labelless divider separates a flagged item (the Administration
            // hub) from the group above it.
            const dividerBefore = item.dividerBefore && index > 0;
            return (
              <div
                key={item.to}
                className={sectionStart || dividerBefore ? "pt-3" : ""}
              >
                {sectionStart ? (
                  isExpanded ? (
                    <p className="px-3 pb-1 text-[10px] text-slate-500 uppercase font-bold tracking-wider truncate">
                      {t(item.section)}
                    </p>
                  ) : (
                    <div className="mx-2 mb-2 border-t border-slate-800" />
                  )
                ) : (
                  dividerBefore && (
                    <div className="mx-2 mb-2 border-t border-slate-800" />
                  )
                )}
                <NavLink
                  to={item.to}
                  end={item.end}
                  onClick={guardNavigation}
                  className={({ isActive }) =>
                    `group relative w-full flex items-center py-2.5 rounded-xl text-sm font-medium transition-colors ${FOCUS_RING} ${
                      isActive
                        ? "bg-indigo-600 text-white shadow-lg shadow-indigo-900/20"
                        : "text-slate-300 hover:bg-slate-800 hover:text-white"
                    }`
                  }
                >
                  <span className={ICON_SLOT}>{item.icon}</span>
                  <span className={labelClass}>{label}</span>
                  {!isExpanded && <RailTooltip label={label} />}
                </NavLink>
              </div>
            );
          })}
        </nav>
      </div>

      <div className="mt-auto border-t border-slate-800 min-w-0 px-3 py-4">
        <NavLink
          to="/profile"
          onClick={guardNavigation}
          className={({ isActive }) =>
            `group relative flex items-center py-2 rounded-xl transition-colors ${FOCUS_RING} ${
              isActive ? "bg-slate-800" : "hover:bg-slate-800"
            }`
          }
        >
          <span className={ICON_SLOT}>
            <Avatar
              user={user || {}}
              alt={displayName}
              className="w-9 h-9 rounded-full bg-slate-700 border border-slate-600 shrink-0 text-white text-xs font-bold"
            />
          </span>
          <div className={labelClass}>
            <p className="text-sm font-bold text-white truncate">
              {displayName}
            </p>
            <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider truncate">
              {displayRole}
            </p>
          </div>
          {!isExpanded && <RailTooltip label={displayName} />}
        </NavLink>
        <NavLink
          to="/support"
          onClick={guardNavigation}
          // The badge sits on the icon, because the label is zero-width while
          // the rail is collapsed; the count reaches assistive tech here.
          aria-label={
            supportUnread
              ? t("nav.supportUnread", { n: supportUnread })
              : undefined
          }
          className={({ isActive }) =>
            `group relative mt-2 w-full flex items-center py-2.5 rounded-xl text-sm font-medium transition-colors ${FOCUS_RING} ${
              isActive
                ? "bg-slate-800 text-white"
                : "text-slate-400 hover:bg-slate-800 hover:text-white"
            }`
          }
        >
          <span className={`${ICON_SLOT} relative`}>
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d={SUPPORT_ICON_PATH}
              />
            </svg>
            {supportUnread > 0 && (
              <span
                aria-hidden="true"
                className="absolute -top-2 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-indigo-500 text-white text-[10px] font-black leading-[18px] text-center ring-2 ring-slate-900"
              >
                {supportUnread > 9 ? "9+" : supportUnread}
              </span>
            )}
          </span>
          <span className={`${labelClass} text-left`}>{t("nav.support")}</span>
          {!isExpanded && <RailTooltip label={t("nav.support")} />}
        </NavLink>
        {onLogout && (
          <button
            onClick={handleLogout}
            disabled={signingOut}
            aria-busy={signingOut}
            aria-label={t("common.signOut")}
            className={`group relative mt-2 w-full flex items-center py-2.5 rounded-xl text-slate-400 transition-colors ${
              signingOut
                ? "opacity-50 cursor-wait"
                : "hover:bg-slate-800 hover:text-white"
            } ${FOCUS_RING}`}
          >
            <span className={ICON_SLOT}>
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
            </span>
            <span
              className={`${labelClass} text-[11px] font-bold uppercase tracking-widest text-left`}
            >
              {t("common.signOut")}
            </span>
            {!isExpanded && <RailTooltip label={t("common.signOut")} />}
          </button>
        )}
      </div>
    </aside>
    </>
  );
}
